import { useEffect, useMemo, useState } from "react";
import { Capacitor } from "@capacitor/core";

type RuntimePlatform = "web" | "ios" | "android";
export type IonicBreakpoint = "xs" | "sm" | "md" | "lg" | "xl";

export type DeviceContext = {
  platform: RuntimePlatform;
  isNative: boolean;

  prefersCoarsePointer: boolean;
  breakpoint: IonicBreakpoint;

  // Your direct UI decision for the Timer check-in control:
  useSlideAction: boolean;

  width: number;
  height: number;
};

function getViewport() {
  if (typeof window === "undefined") return { width: 0, height: 0 };
  return { width: window.innerWidth, height: window.innerHeight };
}

/**
 * Ionic breakpoints (defaults):
 * xs < 576
 * sm >= 576
 * md >= 768
 * lg >= 992
 * xl >= 1200
 */
function getIonicBreakpoint(width: number): IonicBreakpoint {
  if (width >= 1200) return "xl";
  if (width >= 992) return "lg";
  if (width >= 768) return "md";
  if (width >= 576) return "sm";
  return "xs";
}

function getCoarsePointer(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(pointer: coarse)").matches;
}

export function useDeviceContext(): DeviceContext {
  const [viewport, setViewport] = useState(getViewport());
  const [prefersCoarsePointer, setPrefersCoarsePointer] = useState(getCoarsePointer());

  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform() as RuntimePlatform;

  useEffect(() => {
    const onResize = () => setViewport(getViewport());
    window.addEventListener("resize", onResize, { passive: true });

    const mql = window.matchMedia?.("(pointer: coarse)");
    const onPointerChange = () => setPrefersCoarsePointer(getCoarsePointer());

    if (mql?.addEventListener) mql.addEventListener("change", onPointerChange);
    else if (mql?.addListener) mql.addListener(onPointerChange);

    return () => {
      window.removeEventListener("resize", onResize);
      if (mql?.removeEventListener) mql.removeEventListener("change", onPointerChange);
      else if (mql?.removeListener) mql.removeListener(onPointerChange);
    };
  }, []);

  const breakpoint = useMemo(
    () => getIonicBreakpoint(viewport.width),
    [viewport.width]
  );

  /**
   * For your Timer check-in button:
   * - Native apps: always slide (touch-first)
   * - Web: slide when touch-first (coarse pointer)
   * - Optional fallback: also slide on smaller Ionic widths (xs/sm),
   *   in case a device reports pointer info oddly.
   */
  const useSlideAction = useMemo(() => {
    if (isNative) return true;
    return (prefersCoarsePointer && breakpoint !== "xl") || breakpoint === "xs" || breakpoint === "sm";
  }, [isNative, prefersCoarsePointer, breakpoint]);

  return {
    platform,
    isNative,
    prefersCoarsePointer,
    breakpoint,
    useSlideAction,
    width: viewport.width,
    height: viewport.height,
  };
}